import type {
  ComposeCampaignRequest,
  VoiceCampaignFormValues,
} from './types';
import { templateFeatures } from './types';

/**
 * The form now stores the actual backend `templateId` ('7' / '8' / '9' for
 * the three TTS flavors), so this is just an identity passthrough — kept as
 * a named export so callers don't have to reach into the form values.
 */
export function resolveTemplateId(values: VoiceCampaignFormValues): string {
  return values.templateId;
}

/** Format a Date or ISO string to the legacy `YYYY-MM-DD HH:mm:ss` shape. */
export function formatScheduleTime(value: string | Date | null): string {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

/**
 * Build the JSON body the legacy `/api/obd/campaign/compose` endpoint
 * expects. `baseId` is resolved separately (uploaded contact/file →
 * baseId; existing → the picked id).
 */
export function buildComposeRequest(args: {
  userId: number;
  values: VoiceCampaignFormValues;
  baseId: number;
  /**
   * Lookup from locationId (string) → locationName. Used to assemble the
   * legacy `location` wire shape `{locationList: [{locationId, locationName}]}`.
   * The backend expects each entry to be a JSON OBJECT, not a bare string —
   * sending raw ids triggers "JSONArray[0] is not a JSONObject" on compose.
   */
  locationNameById?: Record<string, string>;
  /**
   * Lookup from cliId (string) → cli phone number. Same shape requirement
   * as `location` — legacy ships `cliList: [{cliId, cli}, …]`.
   */
  cliNameById?: Record<string, string>;
}): ComposeCampaignRequest {
  const {
    userId,
    values,
    baseId,
    locationNameById = {},
    cliNameById = {},
  } = args;
  const templateId = resolveTemplateId(values);
  const f = templateFeatures(values.templateId);

  // For optional voice files, only ship the PId if the toggle is on AND the
  // file is even applicable to this template.
  const optInclude = (
    name: 'welcome' | 'noinput' | 'wronginput' | 'thanks',
  ): boolean => {
    if (f.requiredVoiceFiles.includes(name)) return true;
    if (!f.optionalVoiceFiles.includes(name)) return false;
    switch (name) {
      case 'welcome':
        return values.useWelcomeFile;
      case 'noinput':
        return values.useNoinputFile;
      case 'wronginput':
        return values.useWronginputFile;
      case 'thanks':
        return values.useThanksFile;
    }
  };

  return {
    userId,
    campaignName: values.campaignName.trim(),
    templateId,
    /*
     * The backend rejects an empty `scheduleTime` with "Invalid Schedule
     * Date and Time !!". Legacy always populates it — when the user hasn't
     * picked a specific date, it sends the current time (treated as "fire
     * now"). Mirror that behavior here.
     */
    scheduleTime:
      values.sendLater && values.scheduleDate
        ? formatScheduleTime(values.scheduleDate)
        : formatScheduleTime(new Date()),
    baseId,
    dtmf: values.dtmf.join(','),
    welcomePId: optInclude('welcome') ? values.welcomeId : '',
    menuPId: f.requiredVoiceFiles.includes('menu') ? values.menuId : '',
    noInputPId: optInclude('noinput') ? values.noinputId : '',
    wrongInputPId: optInclude('wronginput') ? values.wronginputId : '',
    thanksPId: optInclude('thanks') ? values.thanksId : '',
    smsSuccessApi: values.sendSmsSuccess ? values.smsApiSuccess : '',
    smsFailApi: values.sendSmsFail ? values.smsApiFail : '',
    smsDtmfApi: values.sendSmsDtmf ? values.smsApiDtmf : '',
    callDurationSMS: values.sendSmsSuccess ? values.callDurationSMS : 0,
    retries: Number(values.retries) || 0,
    retryInterval: (Number(values.retryInterval) || 0) * 60,
    /**
     * Legacy wire shape is `{ patchList: [{groupId, groupName, agentDtmf}, …] }`
     * stringified — preserve that exactly. Empty for non-patch templates.
     */
    agentRows: f.hasPatchList
      ? JSON.stringify({ patchList: values.patchList ?? [] })
      : '',
    menuWaitTime: values.menuWaitTime,
    rePrompt: values.rePrompt,
    ivrId: values.ivrId,
    /**
     * Legacy wire shape — each entry MUST be `{locationId, locationName}`.
     * The backend `new JSONObject(location).getJSONArray("locationList")
     * .getJSONObject(0)` call rejects bare-string elements with
     * "JSONArray[0] is not a JSONObject".
     */
    location: JSON.stringify({
      locationList: values.locationIds.map((id) => ({
        locationId: id,
        locationName: locationNameById[id] ?? '',
      })),
    }),
    /**
     * Same object-wrapping requirement as `location` — each cli entry must
     * be `{cliId, cli}`. Empty string for "use shared pool".
     */
    clis:
      values.selectedCLI.length === 0
        ? ''
        : JSON.stringify({
            cliList: values.selectedCLI.map((id) => ({
              cliId: id,
              cli: cliNameById[id] ?? '',
            })),
          }),
    webhook: values.sendWebhook,
    webhookId: values.sendWebhook ? values.webhookId : '',
    /**
     * Legacy wire shape is `{ ttsList: [{ttsType, value, variableType}, …] }`
     * stringified — preserve that exactly. Empty for non-TTS templates.
     */
    ttsRows: f.hasTts
      ? JSON.stringify({ ttsList: values.ttsRows ?? [] })
      : '',
    gender: values.gender,
    language: values.language,
    noAgentId: f.requiredVoiceFiles.includes('noAgent') ? values.noAgentId : '',
    callPatchSuccessMessage: values.sendSmsPatchSuccess
      ? values.callPatchSuccessMessage
      : '',
    callPatchFailMessage: values.sendSmsPatchFail
      ? values.callPatchFailMessage
      : '',
    ttsFlowId: values.selectedTTSFlowId,
    repeatMenu: values.repeatMenu,
  };
}
